import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Terminal, AlertCircle, Mail, Lock } from "lucide-react";
import GoogleSignInButton from "../components/GoogleSignInButton";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(u.onboarded ? "/dashboard" : "/onboarding");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grid md:grid-cols-2">
      {/* Left */}
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-zinc-900 bg-black relative overflow-hidden">
        <div className="grid-bg absolute inset-0 opacity-50" />
        <Link to="/" className="relative flex items-center gap-2 z-10" data-testid="login-logo">
          <div className="w-7 h-7 flex items-center justify-center bg-[#00E5FF] text-black">
            <Terminal size={16} strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-lg">
            Hypex<span className="text-[#00E5FF]">Labs</span>
          </span>
        </Link>
        <div className="relative z-10 max-w-md">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00FF41] font-mono mb-4">
            // welcome back
          </div>
          <div className="font-term text-sm space-y-1 text-zinc-400">
            <div><span className="text-[#00FF41]">$</span> whoami</div>
            <div className="text-zinc-500">network_engineer_in_training</div>
            <div><span className="text-[#00FF41]">$</span> cat /etc/plan</div>
            <div className="text-zinc-300">pass CCNA 200-301</div>
            <div><span className="text-[#00FF41]">$</span> ./start_labs.sh</div>
            <div className="text-[#00E5FF]">login_required...</div>
          </div>
        </div>
        <div className="relative z-10 text-xs text-zinc-600 font-mono">
          encrypted · httpOnly session · GDPR-safe
        </div>
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8 flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-[#00E5FF] text-black">
              <Terminal size={16} strokeWidth={2.5} />
            </div>
            <span className="font-display font-black text-lg">HypexLabs</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono mb-2">
            authenticate
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight mb-8">Log in</h1>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs text-zinc-400 uppercase tracking-widest font-mono mb-1 block">email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-9 py-2.5 text-sm outline-none font-mono"
                  placeholder="you@engineer.com"
                  data-testid="login-email"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-zinc-400 uppercase tracking-widest font-mono">password</label>
                <Link to="/forgot-password" className="text-[10px] uppercase tracking-widest font-mono text-zinc-500 hover:text-[#00E5FF] transition-colors" data-testid="login-forgot-link">
                  forgot?
                </Link>
              </div>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-black border border-zinc-800 focus:border-[#00E5FF] px-9 py-2.5 text-sm outline-none font-mono"
                  placeholder="••••••••"
                  data-testid="login-password"
                />
              </div>
            </div>

            {error && (
              <div
                className="flex items-start gap-2 text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 p-2"
                data-testid="login-error"
              >
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00E5FF] text-black font-semibold py-2.5 hover:bg-[#00B8CC] transition-colors disabled:opacity-50"
              data-testid="login-submit"
            >
              {loading ? "authenticating…" : "log in"}
            </button>

            <GoogleSignInButton label="continue with Google" testId="login-google" />
          </form>

          <div className="mt-6 text-sm text-zinc-500">
            new here?{" "}
            <Link to="/signup" className="text-[#00E5FF] hover:underline" data-testid="login-to-signup">
              create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
